import React, { useState, useEffect } from 'react';
import { XMarkIcon, Cog6ToothIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { dspApi } from '../../repositories/dsp';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGoToSettings: () => void;
}

const WelcomeModal: React.FC<WelcomeModalProps> = ({
  isOpen,
  onClose,
  onGoToSettings
}) => {
  const [connectedDSPs, setConnectedDSPs] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      checkDSPConnections();
    }
  }, [isOpen]);

  const checkDSPConnections = async () => {
    try {
      const response = await dspApi.getDSPs();
      setConnectedDSPs(response.data.data?.length || 0);
    } catch (error) {
      setConnectedDSPs(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePrivacyPolicyClick = () => {
    window.open('/privacy-policy', '_blank', 'noopener,noreferrer');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md sm:max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Cog6ToothIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                Welcome to Cadence IO!
              </h2>
              <p className="text-sm text-gray-600">Let's get you set up</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {!loading && connectedDSPs > 0 ? (
            // User already has DSP connections
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircleIcon className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                You're All Set!
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                You already have {connectedDSPs} DSP account{connectedDSPs !== 1 ? 's' : ''} connected. 
                You can manage your connections or add more in the settings.
              </p>
              <div className="space-y-3">
                <button
                  onClick={onGoToSettings}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base"
                >
                  Manage DSP Settings
                </button>
                <button
                  onClick={onClose}
                  className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm sm:text-base"
                >
                  Continue to Dashboard
                </button>
              </div>
            </div>
          ) : (
            // User needs to set up DSP connections
            <>
              <div className="mb-6">
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3">
                  Connect Your DSP Accounts
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed mb-4">
                  To get the most out of Cadence IO, we recommend connecting your Digital Service Provider (DSP) accounts. This will help us:
                </p>
                <ul className="list-disc ml-5 space-y-1 text-sm sm:text-base text-gray-600">
                  <li>Fetch your streaming data automatically</li>
                  <li>Improve pitch targeting accuracy</li>
                  <li>Provide better analytics and insights</li>
                  <li>Streamline your workflow</li>
                </ul>
              </div>

              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-1">
                      Secure & Private
                    </p>
                    <p className="text-sm text-blue-800">
                      Your login credentials are encrypted and stored securely. You can{' '}
                      <button
                        onClick={handlePrivacyPolicyClick}
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                      >
                        review our privacy policy
                      </button>
                      {' '}to learn more about how we protect your data.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={onGoToSettings}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base flex items-center justify-center gap-2"
                >
                  <Cog6ToothIcon className="w-5 h-5" />
                  Connect DSP Accounts
                </button>
                
                <button
                  onClick={onClose}
                  className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm sm:text-base"
                >
                  Skip for Now
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 rounded-b-xl border-t border-gray-100">
          <p className="text-xs sm:text-sm text-gray-500 text-center">
            You can always connect your DSP accounts later in Settings → DSPs
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;